
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Coins } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import { TOKEN_PACKAGES } from './constants';
import TokenPackageList from './TokenPackageList';
import StatusMessage from './StatusMessage';

interface TokenPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
}

const TokenPurchaseModal: React.FC<TokenPurchaseModalProps> = ({
  isOpen,
  onClose,
  currentBalance
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { refreshBalance } = useTokenBalance();

  const handlePurchase = async (packageId: string) => {
    const selectedPackage = TOKEN_PACKAGES.find(p => p.id === packageId);
    if (!selectedPackage) return;

    setLoading(packageId);
    setError(null);
    setSuccessMessage(null);

    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          packageId: selectedPackage.id,
          tokens: selectedPackage.tokens,
          price: selectedPackage.price
        }
      });

      if (error) {
        console.error('Error creating checkout session:', error);
        if (error.message && error.message.includes('STRIPE_SECRET_KEY')) {
          setError('Payment system is not configured. Please contact support.');
        } else {
          setError('Failed to create checkout session. Please try again.');
        }
        setLoading(null);
        return;
      }

      if (data?.url) {
        // Store purchase attempt for tracking
        localStorage.setItem('pending_token_purchase', JSON.stringify({
          packageId: selectedPackage.id,
          tokens: selectedPackage.tokens,
          timestamp: Date.now()
        }));
        
        // Open Stripe checkout in new tab
        const checkoutWindow = window.open(data.url, '_blank');
        
        // Monitor for purchase completion
        const checkPurchaseCompletion = setInterval(async () => {
          if (checkoutWindow?.closed) {
            console.log('💰 TokenPurchaseModal: Checkout window closed, checking for purchase completion');
            clearInterval(checkPurchaseCompletion);
            
            // Wait a moment for webhook processing
            setTimeout(async () => {
              await refreshBalance();
              
              // Check if purchase was completed
              const pendingPurchase = localStorage.getItem('pending_token_purchase');
              if (pendingPurchase) {
                localStorage.removeItem('pending_token_purchase');
                // Trigger storage event for other tabs
                localStorage.setItem('token_purchase_completed', Date.now().toString());
                localStorage.removeItem('token_purchase_completed');
                
                setSuccessMessage(`Successfully purchased ${selectedPackage.tokens.toLocaleString()} tokens!`);
                setTimeout(() => {
                  setSuccessMessage(null);
                  onClose();
                }, 3000);
              }
            }, 2000);
          }
        }, 1000);
        
      } else {
        setError('No checkout URL received. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const formatTokens = (tokens: number) => {
    return tokens.toLocaleString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            Buy More Tokens
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">Current Balance</p>
            <p className="text-2xl font-bold text-primary">
              {formatTokens(currentBalance)} tokens
            </p>
          </div>

          {successMessage && (
            <StatusMessage type="success" message={successMessage} />
          )}

          {error && (
            <StatusMessage type="error" message={error} />
          )}

          <TokenPackageList
            loading={loading}
            onPurchase={handlePurchase}
            formatTokens={formatTokens}
          />

          <div className="text-xs text-muted-foreground text-center">
            <p>• Tokens never expire</p>
            <p>• Secure payment via Stripe</p>
            <p>• Instant delivery</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TokenPurchaseModal;
